import React, { useState, useMemo } from 'react'
import { useCurrency } from '../contexts/CurrencyContext'
import { formatPrice } from '../utils/currencyUtils'

const FavoriteProfessionalModal = ({
  isOpen,
  onClose,
  professional,
  services,
  categories,
  onConfirm
}) => {
  const { currency } = useCurrency()
  const [selectedServices, setSelectedServices] = useState([])

  const toggleService = (service) => {
    setSelectedServices(prev => {
      const isSelected = prev.some(s => s.id === service.id)
      if (isSelected) {
        return prev.filter(s => s.id !== service.id)
      } else {
        return [...prev, service]
      }
    })
  }

  const handleConfirm = () => {
    if (selectedServices.length > 0) {
      onConfirm(selectedServices)
      setSelectedServices([])
      onClose()
    }
  }

  // Get all services from categories
  const allServices = services && services.length > 0 
    ? services 
    : categories?.flatMap(cat => cat.services || []) || []

  // Filter services based on professional's specializations
  const relevantServices = useMemo(() => {
    if (!professional || !allServices || allServices.length === 0) return allServices
    
    // Get professional's specializations
    const specializations = professional.specializations || []
    
    // If professional has no specializations, show all services
    if (!Array.isArray(specializations) || specializations.length === 0) {
      return allServices
    }
    
    // Extract category names and IDs from specializations
    const specializationNames = specializations
      .map(spec => {
        if (typeof spec === 'string') return spec.toLowerCase()
        if (typeof spec === 'object' && spec !== null) {
          return (spec.name || spec.category_name || '').toLowerCase()
        }
        return null
      })
      .filter(Boolean)
    
    const specializationIds = specializations
      .map(spec => {
        if (typeof spec === 'object' && spec !== null) {
          return spec.id || spec.category_id
        }
        return null
      })
      .filter(Boolean)
    
    // Filter services that belong to categories the professional specializes in
    return allServices.filter(service => {
      // Get service's category
      const serviceCategoryId = service.category_id
      const serviceCategoryName = service.category_name
      
      // Find the category object
      let serviceCategory = null
      if (serviceCategoryId) {
        serviceCategory = categories?.find(cat => cat.id === serviceCategoryId)
      }
      if (!serviceCategory && serviceCategoryName) {
        serviceCategory = categories?.find(cat => cat.name === serviceCategoryName)
      }
      // If still not found, search through categories
      if (!serviceCategory && categories) {
        for (const cat of categories) {
          if (cat.services && Array.isArray(cat.services)) {
            const found = cat.services.find(s => s.id === service.id)
            if (found) {
              serviceCategory = cat
              break
            }
          }
        }
      }
      
      // If we can't determine the category, include the service
      if (!serviceCategory) return true
      
      // Check if the service's category matches any of the professional's specializations
      const categoryName = serviceCategory.name?.toLowerCase()
      const categoryId = serviceCategory.id
      
      // Match by name
      if (categoryName && specializationNames.some(specName => 
        specName === categoryName || 
        categoryName.includes(specName) || 
        specName.includes(categoryName)
      )) {
        return true
      }
      
      // Match by ID
      if (categoryId && specializationIds.some(specId => 
        String(specId) === String(categoryId)
      )) {
        return true
      }
      
      return false
    })
  }, [professional, allServices, categories])

  // If no services match, fallback to all services
  const displayServices = relevantServices.length > 0 
    ? relevantServices 
    : allServices

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Select Services</h3>
            <p className="text-sm text-gray-600 mt-1">
              Choose services for <span className="font-semibold">{professional?.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
        
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {displayServices.length > 0 ? (
            <div className="space-y-2">
              {displayServices.map((service) => {
                const isSelected = selectedServices.some(s => s.id === service.id)
                return (
                  <button
                    key={service.id}
                    onClick={() => toggleService(service)}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-primary bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{service.name}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          {service.duration && (
                            <span><i className="far fa-clock mr-1"></i>{service.duration}</span>
                          )}
                          {service.price !== undefined && (
                            <span className="text-primary font-semibold">
                              {formatPrice(parseFloat(service.price || 0), currency)}
                            </span>
                          )}
                        </div>
                        {service.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{service.description}</p>
                        )}
                      </div>
                      {isSelected && (
                        <i className="fas fa-check-circle text-primary text-xl ml-3"></i>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No services available</p>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedServices.length === 0}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-purple-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to Favorites ({selectedServices.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FavoriteProfessionalModal

